/**
 * Leaguemate Scouting (`/scout`) — S1 configuration.
 *
 * All tunables live here as NAMED constants, not magic numbers. The
 * similarity weights and breakpoints are PROVISIONAL: they were validated
 * on a 25-leaguemate calibration sample (audit 2026-05-31, see
 * LEAGUEMATE_SCOUTING_v1.md §5/§7) and are expected to be re-fit against a
 * larger sample before hardening. Keep them as config so that re-fit is a
 * one-line edit, never a code change.
 */

// --- Format-similarity weights (v1 spec §5) -------------------------------
// sim = 1
//   − superflex·|SUPER_FLEX slot diff|     (QB-slot mismatch dominates: 1QB vs SF
//                                            reshapes the whole value board)
//   − ppr·|rec diff|
//   − tep·(TE-premium on/off differs ? 1 : 0)
//   − rosterLen·(|roster_positions length diff| / 10)
//   − teamCount·(|total_rosters diff| / 12)
// clamped to [0,1].
export const SIM_WEIGHTS = {
  superflex: 0.50,
  ppr:       0.20,
  tep:       0.15,
  rosterLen: 0.10,
  teamCount: 0.05,
};

// Source leagues below this similarity to the target are EXCLUDED from the
// affinity pull (not merely down-weighted). The single most important
// noise-control lever.
export const SIM_FLOOR = 0.35;

// Per-manager blast-radius caps (v1 spec §3.2 two-tier pull).
export const MAX_LEAGUES_PER_MGR = 12; // affinity/roster set (cheap: 1 call/league)
export const TX_LEAGUES_PER_MGR  = 6;  // transaction set (expensive ~19 calls/league),
                                       // marked here, consumed in S3.

// Season scope: current + prior (v1 spec §11 Q2).
export const SCOUT_SEASONS = ["2026", "2025"];

// --- Affinity (S2 believers) ----------------------------------------------
// Only these positions are tradeable skill assets. DEF (team-code ids like
// "DET") and K are filtered out of believer lists.
export const SKILL_POSITIONS = ["QB", "RB", "WR", "TE"];
// Below this many usable franchises a manager's read is too thin to present
// confidently — flag the whole card "limited data" (v1 spec §7b honesty guard).
export const AFFINITY_MIN_FRANCHISES = 2;
// A manager is "high confidence" at/above this many usable franchises.
export const AFFINITY_HIGH_CONFIDENCE_FRANCHISES = 4;

// --- Deep dive (S3 transactions tier) -------------------------------------
// Sleeper settings.type: 0 redraft · 1 keeper · 2 dynasty · 3 other.
// Trade-style / FAAB / rebuild read only over dynasty+keeper, non-best-ball
// (§3.0.1). Redraft/best-ball rosters reset → no cross-season conviction.
export const DYNASTY_KEEPER_TYPES = [1, 2];
// Transaction-set cap for the deep dive (same intent as TX_LEAGUES_PER_MGR;
// applied AFTER the dynasty/keeper filter so the 6 are all valid TX leagues).
export const DEEP_TX_LEAGUES = 6;
export const DEEP_TX_MAX_WEEK = 18;          // scan transactions weeks 0..18
export const DEEP_TX_EMPTY_WEEK_STREAK = 2;  // short-circuit after N empty weeks

// FAAB posture bands — spent% over the IDENTICAL tx league set (§7c fix).
// Provisional (25-leaguemate calibration); re-fit on a larger sample.
export const FAAB_HOARDER_MAX_PCT    = 8;   // < 8%  → hoarder
export const FAAB_AGGRESSIVE_MIN_PCT = 40;  // > 40% → aggressive (else balanced)

// Reachability (gating). Bimodal in the audit: median 17d then cliff to 158d.
export const REACH_ACTIVE_MAX_DAYS  = 30;   // ≤30d  → active
export const REACH_COOLING_MAX_DAYS = 90;   // ≤90d  → cooling (else dormant)

// Initiation rate (creator===uid / trades). Only meaningful with enough trades.
export const TRADE_CONFIDENCE_MIN   = 4;    // <4 trades → low-confidence, don't band
export const INIT_REACTIVE_MAX      = 0.25; // <0.25 → reactive
export const INIT_PROACTIVE_MIN     = 0.55; // >0.55 → proactive (else balanced)

// Trade activity, NORMALIZED per league-season (§7c — raw count is confounded
// by fan-out). Provisional normalized bands (raw §7b bands were per-manager).
export const ACTIVITY_LOW_MAX       = 0.5;  // trades/(league·season)
export const ACTIVITY_MODERATE_MAX  = 1.5;
export const ACTIVITY_HIGH_MAX      = 3.0;  // above → hyperactive

// Net-affinity combination weights (§4). Believer rate ∈ [0,1]; the trade
// terms are normalized to [0,1] per manager. Provisional.
export const NET_AFFINITY_WEIGHTS = {
  roster: 1.0, // w_roster · weightedRosterRate
  acq:    0.3, // w_acq    · normalizedNetAcquisitions (acquired via trade = conviction)
  away:   0.5, // w_away   · normalizedTradedAway (dealt away = low conviction)
};
// Trade-signal handling (DECIDED after S3 — sparsity is structural §4):
// - Acquisition boost is CAPPED so a single trade can only elevate a believer
//   WITHIN its rate tier, never leapfrog dense multi-league conviction
//   (the Mike-Evans-#1-off-one-trade bug). Principled value-surrendered
//   weighting + final magnitudes → larger-sample pass (§7d).
export const ACQ_BOOST_CAP    = 0.15;
// - Trade-away is a flat FLAG penalty, not a fake gradient (normalizedTradedAway
//   collapses to ~1.0 at any scale). Don't-value list is ranked by player VALUE.
export const AWAY_FLAG_PENALTY = 0.10;

// --- Win-now ↔ rebuild (S3.5, §7c) ----------------------------------------
// net_future_picks = future picks (season ≥ current) acquired − sent, over the
// (≤6) tx set — the SAME league scale as the audit's tx-set netPicks, so the
// raw bands below map to the validated examples (izzye03 +49 → rebuild,
// kodypetey −15 → win-now). netPerLeague is also surfaced as the fan-out-fair
// metric the larger-sample pass should re-fit bands against (§7c carry).
export const REBUILD_NET_MIN  = 4;   // net ≥ +4 → rebuild intent
export const WINNOW_NET_MAX    = -4; // net ≤ −4 → win-now intent
// youth-tilt = youth_share − vet_share (% roster age ≤23 minus % ≥29).
export const YOUTH_AGE_MAX     = 23; // ≤23 counts as youth
export const VET_AGE_MIN       = 29; // ≥29 counts as vet
export const YOUTH_TILT_STRONG = 12; // tilt > +12 → young roster (state)
export const WINNOW_TILT_MAX   = 0;  // tilt < 0   → win-now-leaning roster (state)
// net_future_picks is only a reliable INTENT signal over enough leagues — a
// single tx league's pick total is fragile (S3.5 calibration note). Below this,
// the win-now/rebuild read leans on roster youth-tilt (state) and cannot reach
// high confidence → the archetype crown for it is withheld.
export const REBUILD_INTENT_MIN_TX_LEAGUES = 2;

// --- Positional lean (S3.5, §7c) — RB/WR/TE share of skill players ---------
export const POS_RB_HEAVY_MIN   = 42; // RB ≥ 42% → RB-heavy
export const POS_ZERO_RB_MAX    = 28; // RB ≤ 28% → zero-RB-ish
export const POS_WR_HEAVY_MIN   = 50; // WR ≥ 50% → WR-heavy
export const POS_TE_BELIEVER_MIN = 26; // TE ≥ 26% → TE-believer
export const ROOKIE_DRAFT_MAX_ROUNDS = 6; // drafts with ≤6 rounds are rookie drafts

// --- Throttle / concurrency / backoff -------------------------------------

// --- Throttle / concurrency / backoff -------------------------------------
// Sleeper's binding constraint is 1000 calls/min → IP block. We target well
// under it: ~120ms minimum spacing between request STARTS (~500/min) with at
// most 5 concurrent in flight, exponential backoff on HTTP 429.
export const FETCH_THROTTLE_MS  = 120;
export const FETCH_CONCURRENCY  = 5;
export const FETCH_MAX_RETRIES  = 4;
export const FETCH_BACKOFF_BASE_MS = 1000; // 1s, 2s, 4s, 8s on repeated 429s
